import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { canSeeAmounts } from '@/lib/roles';
import { fmtDate, fmtINR } from '@/lib/format';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { useBulkSelection } from '@/lib/useBulkSelection';
import { Plus, TrendingUp, CalendarClock, Percent, IndianRupee, ArrowRightLeft } from 'lucide-react';

const BULK_ROLES = ['owner', 'operations', 'accounts'];

const STATUS_LABEL = {
  draft: 'Draft', shared: 'Shared', under_negotiation: 'Under Negotiation',
  won: 'Won', lost: 'Lost', expired: 'Expired', on_hold: 'On Hold',
};
const STATUS_CHIP = {
  draft: '', shared: 'chip-info', under_negotiation: 'chip-warning',
  won: 'chip-success', lost: 'chip-danger', expired: 'chip-danger', on_hold: '',
};

export default function QuoteRegisterPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tiles, setTiles] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(null); // quote for status/link
  const [statusForm, setStatusForm] = useState({ status: 'shared', comment: '' });

  const canWrite = canSeeAmounts(user.role) && ['owner', 'accounts', 'commercials'].includes(user.role);
  const canBulk = BULK_ROLES.includes(user.role);
  const sel = useBulkSelection(rows);

  const load = async () => {
    try {
      const r = await api.get('/quotes'); setRows(r.data || []);
      if (canWrite) { const t = await api.get('/quotes/tiles'); setTiles(t.data); }
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefetch(load, { collections: ['quotes', 'work_orders', 'master_projects'] });

  const submitStatus = async () => {
    try {
      await api.post(`/quotes/${openEdit.id}/status`, statusForm);
      toast.success(`Status → ${STATUS_LABEL[statusForm.status]}`);
      setOpenEdit(null); load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Quote Register' }]}>
      <div className="p-6 max-w-[1300px] mx-auto fade-up" data-testid="quote-register-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Quote Register</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Pre-work-order pipeline. Values are Owner / Accounts / Commercials only.</p>
          </div>
          {canWrite && <Button onClick={() => setOpenNew(true)} data-testid="quote-new-cta"><Plus size={14} className="mr-1" />New quote</Button>}
        </div>

        {tiles && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="quote-tiles">
            <Tile icon={TrendingUp} label="Open pipeline" value={tiles.open_pipeline_count} hint={fmtINR(tiles.open_pipeline_value)} />
            <Tile icon={Percent} label="Win rate (12m)" value={`${tiles.win_rate_12m}%`} />
            <Tile icon={CalendarClock} label="Avg days to decision" value={tiles.avg_days_to_decision} />
            <Tile icon={IndianRupee} label="Follow-ups due today" value={tiles.followups_due_today} />
          </div>
        )}

        <div className="card overflow-x-auto">
          {canBulk && (
            <div className="p-3 pb-0">
              <BulkActionBar
                module="quotes" moduleLabel="Quotes"
                selectedIds={sel.selectedIds} onClear={sel.clear} onApplied={load}
                needsApproval={user.role !== 'owner'}
                editableFields={['status', 'record_state']}
              />
            </div>
          )}
          <table className="tbl" data-testid="quote-table">
            <thead>
              <tr>
                {canBulk && (
                  <th style={{ width: 32 }}>
                    <input type="checkbox" checked={sel.allChecked}
                      ref={el => el && (el.indeterminate = sel.indeterminate)}
                      onChange={sel.toggleAll} aria-label="Select all"
                      data-testid="bulk-select-all-quotes" />
                  </th>
                )}
                <th>Quote #</th><th>Client</th><th>Opportunity</th><th>Value</th><th>Status</th><th>Shared</th><th>Validity</th><th>Next follow-up</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={canBulk ? 10 : 9} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No quotes yet.</td></tr>}
              {rows.map(q => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                  {canBulk && (
                    <td>
                      <input type="checkbox" checked={sel.isSelected(q.id)}
                        onChange={() => sel.toggle(q.id)}
                        aria-label={`Select ${q.quote_number}`}
                        data-testid={`quote-select-${q.id}`} />
                    </td>
                  )}
                  <td className="font-mono text-[12px]">{q.quote_number}</td>
                  <td>{q.client_name}</td>
                  <td>
                    {q.opportunity_name}
                    {q.linked_project_id && (
                      <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
                        Linked · Quoted <MaskedAmount amount={q.value_inr} masked={q.value_masked} /> vs WO <MaskedAmount amount={q.wo_value_inr} masked={q.value_masked} />
                      </div>
                    )}
                  </td>
                  <td><MaskedAmount amount={q.value_inr} masked={q.value_masked} /></td>
                  <td><span className={`chip ${STATUS_CHIP[q.status] || ''}`}>{STATUS_LABEL[q.status] || q.status}</span></td>
                  <td className="text-[12.5px]">{fmtDate(q.date_shared)}</td>
                  <td className="text-[12.5px]">{q.validity_days ? `${q.validity_days} d` : '—'}</td>
                  <td className="text-[12.5px]">{fmtDate(q.next_followup)}</td>
                  <td className="text-right">
                    {canWrite && <Button size="sm" variant="outline" onClick={() => { setOpenEdit(q); setStatusForm({ status: q.status, comment: '' }); }} data-testid={`quote-status-${q.id}`}>Update</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {openNew && <NewQuoteDialog onClose={() => setOpenNew(false)} onSaved={load} />}

        <Dialog open={!!openEdit} onOpenChange={o => !o && setOpenEdit(null)}>
          <DialogContent data-testid="quote-status-dialog">
            <DialogHeader><DialogTitle>Update status — {openEdit?.quote_number}</DialogTitle></DialogHeader>
            <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Status</span>
              <select className="mt-1 w-full h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                      value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })} data-testid="quote-status-select">
                {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block mt-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Comment (required)</span>
              <Textarea rows={3} value={statusForm.comment} onChange={e => setStatusForm({ ...statusForm, comment: e.target.value })} data-testid="quote-status-comment" />
            </label>
            {openEdit && openEdit.status === 'won' && !openEdit.linked_project_id && (
              <div className="mt-2 text-[12.5px] text-[hsl(var(--foreground-muted))] flex items-center gap-1">
                <ArrowRightLeft size={13} /> After marking Won, open a Work Order and link it via the Projects screen.
              </div>
            )}
            <div className="mt-3 max-h-40 overflow-y-auto text-[12px] space-y-1" data-testid="quote-activity-log">
              {(openEdit?.activity || []).slice().reverse().map((a, i) => (
                <div key={i} className="font-mono text-[hsl(var(--foreground-muted))]">{fmtDate(a.at)} · {a.actor} · <b>{a.action}</b> — {a.comment}</div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenEdit(null)}>Cancel</Button>
              <Button onClick={submitStatus} disabled={!statusForm.comment.trim()} data-testid="quote-status-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

const Tile = ({ icon: Icon, label, value, hint }) => (
  <div className="card p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
        <div className="font-heading text-2xl mt-1 tabular-nums">{value}</div>
        {hint && <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">{hint}</div>}
      </div>
      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground-muted))' }}><Icon size={16} /></div>
    </div>
  </div>
);

function NewQuoteDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ client_name: '', opportunity_name: '', value_inr: '', date_shared: '', validity_days: 30, scope_summary: '', next_followup: '' });
  const submit = async () => {
    try {
      await api.post('/quotes', {
        ...f,
        value_inr: f.value_inr === '' ? null : Number(f.value_inr),
        validity_days: f.validity_days === '' ? null : Number(f.validity_days),
      });
      toast.success('Quote created'); onClose(); onSaved();
    } catch (e) { toast.error(parseErr(e)); }
  };
  return (
    <Dialog open={true} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="quote-new-dialog">
        <DialogHeader><DialogTitle>New quote</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Client name</span><Input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} data-testid="quote-new-client" /></label>
          <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Opportunity name</span><Input value={f.opportunity_name} onChange={e => setF({ ...f, opportunity_name: e.target.value })} data-testid="quote-new-opp" /></label>
          <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Value (₹)</span><Input type="number" value={f.value_inr} onChange={e => setF({ ...f, value_inr: e.target.value })} data-testid="quote-new-value" /></label>
          <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Date shared</span><Input type="date" value={f.date_shared} onChange={e => setF({ ...f, date_shared: e.target.value })} /></label>
          <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Validity (days)</span><Input type="number" value={f.validity_days} onChange={e => setF({ ...f, validity_days: e.target.value })} /></label>
          <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Next follow-up</span><Input type="date" value={f.next_followup} onChange={e => setF({ ...f, next_followup: e.target.value })} /></label>
          <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Scope summary</span><Textarea rows={2} value={f.scope_summary} onChange={e => setF({ ...f, scope_summary: e.target.value })} /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!f.client_name.trim() || !f.opportunity_name.trim()} data-testid="quote-new-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
